/**
 * Collection Filtering and Sorting
 * Handles filter sidebar, price slider, search, and sorting functionality
 */

class CollectionFilters {
  constructor() {
    this.filterForm = document.querySelector('.collection-filters-form');
    this.productGrid = document.querySelector('.collection-product-grid');
    this.filterCheckboxes = document.querySelectorAll('.filter-checkbox');
    this.sortButtons = document.querySelectorAll('.sort-btn');
    this.searchForm = document.querySelector('.collection-search-form');
    this.clearAllButton = document.getElementById('clear-all-filters');
    this.minPriceSlider = document.getElementById('min-price-slider');
    this.maxPriceSlider = document.getElementById('max-price-slider');
    this.minPriceDisplay = document.getElementById('min-price-display');
    this.maxPriceDisplay = document.getElementById('max-price-display');
    this.mobileFilterToggle = document.querySelector('.mobile-filter-toggle');
    this.filtersContainer = document.getElementById('collection-filters');

    this.init();
  }

  init() {
    // Initialize mobile filter toggle
    if (this.mobileFilterToggle && this.filtersContainer) {
      this.initMobileFilterToggle();
    }

    // Initialize price slider
    if (this.minPriceSlider && this.maxPriceSlider) {
      this.initPriceSlider();
    }

    // Bind filter checkboxes
    this.filterCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleFilterChange());
    });

    // Bind sort buttons
    this.sortButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleSortChange(e));
    });

    // Bind search form
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', (e) => this.handleSearch(e));
    }

    // Bind clear all filters
    if (this.clearAllButton) {
      this.clearAllButton.addEventListener('click', () => this.clearAllFilters());
    }

    // Bind keyword remove buttons
    this.initKeywordRemoveButtons();

    // Initialize from URL parameters
    this.initFromURL();
  }

  initMobileFilterToggle() {
    this.mobileFilterToggle.addEventListener('click', () => {
      const isExpanded = this.mobileFilterToggle.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        // Collapse filters
        this.mobileFilterToggle.setAttribute('aria-expanded', 'false');
        this.filtersContainer.classList.remove('expanded');
      } else {
        // Expand filters
        this.mobileFilterToggle.setAttribute('aria-expanded', 'true');
        this.filtersContainer.classList.add('expanded');
      }
    });
  }

  initPriceSlider() {
    const minSlider = this.minPriceSlider;
    const maxSlider = this.maxPriceSlider;
    const minDisplay = this.minPriceDisplay;
    const maxDisplay = this.maxPriceDisplay;

    // Update displays on page load
    this.updatePriceDisplay();

    // Handle min slider changes
    minSlider.addEventListener('input', (e) => {
      const minVal = parseInt(minSlider.value);
      const maxVal = parseInt(maxSlider.value);
      const maxPrice = parseInt(maxSlider.max);

      // Prevent sliders from crossing
      if (maxVal - minVal < 5) {
        minSlider.value = maxVal - 5;
      }

      this.updatePriceDisplay();
    });

    // Handle max slider changes
    maxSlider.addEventListener('input', (e) => {
      const minVal = parseInt(minSlider.value);
      const maxVal = parseInt(maxSlider.value);

      // Prevent sliders from crossing
      if (maxVal - minVal < 5) {
        maxSlider.value = minVal + 5;
      }

      this.updatePriceDisplay();
    });

    // Apply filter on change (when user releases slider)
    minSlider.addEventListener('change', () => this.handleFilterChange());
    maxSlider.addEventListener('change', () => this.handleFilterChange());
  }

  updatePriceDisplay() {
    const minVal = parseInt(this.minPriceSlider.value);
    const maxVal = parseInt(this.maxPriceSlider.value);
    const maxPrice = parseInt(this.maxPriceSlider.max);

    this.minPriceDisplay.textContent = `$${minVal}`;
    this.maxPriceDisplay.textContent = maxVal >= maxPrice ? 'Any' : `$${maxVal}`;

    // Update slider range fill (visual feedback)
    const percentMin = (minVal / maxPrice) * 100;
    const percentMax = (maxVal / maxPrice) * 100;

    const sliderRange = document.querySelector('.price-slider-range');
    if (sliderRange) {
      sliderRange.style.left = `${percentMin}%`;
      sliderRange.style.width = `${percentMax - percentMin}%`;
    }
  }

  initKeywordRemoveButtons() {
    const keywordTags = document.querySelectorAll('.keyword-tag');
    keywordTags.forEach(tagLink => {
      tagLink.addEventListener('click', (e) => {
        // The keyword-tag is an <a> link that navigates to remove the tag
        // No need to prevent default or add custom logic here
        // Shopify handles tag removal via URL
      });
    });
  }

  removeTag(tag) {
    // Get current URL
    const url = new URL(window.location);
    const currentPath = url.pathname;

    // Parse current tags from URL
    const pathParts = currentPath.split('/');
    const tagIndex = pathParts.indexOf('tagged');

    if (tagIndex !== -1 && pathParts[tagIndex + 1]) {
      const currentTags = pathParts[tagIndex + 1].split('+');
      const updatedTags = currentTags.filter(t => t !== tag);

      if (updatedTags.length > 0) {
        pathParts[tagIndex + 1] = updatedTags.join('+');
      } else {
        // Remove 'tagged' segment if no tags left
        pathParts.splice(tagIndex, 2);
      }

      const newPath = pathParts.join('/');
      window.location.href = newPath + url.search;
    }
  }

  handleFilterChange() {
    const url = this.buildFilterURL();
    window.location.href = url;
  }

  handleSortChange(e) {
    const button = e.currentTarget;
    const sortValue = button.dataset.sort;

    // Update active state
    this.sortButtons.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    // Build URL with sort parameter
    const url = this.buildFilterURL(sortValue);
    window.location.href = url;
  }

  handleSearch(e) {
    e.preventDefault();
    const searchInput = this.searchForm.querySelector('input[name="q"]');
    const searchTerm = searchInput.value.trim();

    if (searchTerm) {
      const url = new URL(window.location);
      url.searchParams.set('q', searchTerm);
      window.location.href = url.toString();
    }
  }

  clearAllFilters() {
    // Get base collection URL without any filters
    const url = new URL(window.location);
    const pathParts = url.pathname.split('/');

    // Remove tagged segment
    const tagIndex = pathParts.indexOf('tagged');
    if (tagIndex !== -1) {
      pathParts.splice(tagIndex, 2);
    }

    // Build clean URL
    const cleanPath = pathParts.join('/');
    window.location.href = cleanPath;
  }

  buildFilterURL(sortValue = null) {
    const url = new URL(window.location);

    // Get base collection path (clean, no tagged segment)
    let path = url.pathname.split('/tagged')[0].split('?')[0];

    // Build search params using ONLY Shopify Storefront Filtering API
    const params = new URLSearchParams();

    // Product type filters - use original values
    const typeCheckboxes = document.querySelectorAll('.filter-checkbox[data-filter-type="type"]:checked');
    typeCheckboxes.forEach(checkbox => {
      const originalValue = checkbox.dataset.originalValue || checkbox.value;
      params.append('filter.p.product_type', originalValue);
    });

    // Vendor filters
    const vendorCheckboxes = document.querySelectorAll('.filter-checkbox[data-filter-type="vendor"]:checked');
    vendorCheckboxes.forEach(checkbox => {
      const originalValue = checkbox.dataset.originalValue || checkbox.value;
      params.append('filter.p.vendor', originalValue);
    });

    // Color filters - use variant options
    const colorCheckboxes = document.querySelectorAll('.filter-checkbox[data-filter-type="color"]:checked');
    colorCheckboxes.forEach(checkbox => {
      const originalValue = checkbox.dataset.originalValue || checkbox.value;
      params.append('filter.v.option.color', originalValue);
    });

    // Size filters - use variant options
    const sizeCheckboxes = document.querySelectorAll('.filter-checkbox[data-filter-type="size"]:checked');
    sizeCheckboxes.forEach(checkbox => {
      const originalValue = checkbox.dataset.originalValue || checkbox.value;
      params.append('filter.v.option.size', originalValue);
    });

    // Gender filters - use variant options
    const genderCheckboxes = document.querySelectorAll('.filter-checkbox[data-filter-type="gender"]:checked');
    genderCheckboxes.forEach(checkbox => {
      const originalValue = checkbox.dataset.originalValue || checkbox.value;
      params.append('filter.v.option.gender', originalValue);
    });

    // Tag filters
    const tagCheckboxes = document.querySelectorAll('.filter-checkbox[data-filter-type="tag"]:checked');
    tagCheckboxes.forEach(checkbox => {
      const originalValue = checkbox.dataset.originalValue || checkbox.value;
      params.append('filter.p.tag', originalValue);
    });

    // Add price filter
    if (this.minPriceSlider && this.maxPriceSlider) {
      const minPrice = parseInt(this.minPriceSlider.value);
      const maxPrice = parseInt(this.maxPriceSlider.value);
      const maxLimit = parseInt(this.maxPriceSlider.max);

      if (minPrice > 0 || maxPrice < maxLimit) {
        params.append('filter.v.price.gte', minPrice);
        if (maxPrice < maxLimit) {
          params.append('filter.v.price.lte', maxPrice);
        }
      }
    }

    // Add sort parameter
    if (sortValue) {
      params.append('sort_by', sortValue);
    } else {
      // Preserve existing sort
      const currentSort = url.searchParams.get('sort_by');
      if (currentSort) {
        params.append('sort_by', currentSort);
      }
    }

    // Preserve search query
    const searchQuery = url.searchParams.get('q');
    if (searchQuery) {
      params.append('q', searchQuery);
    }

    // Build final URL
    const paramString = params.toString();
    return path + (paramString ? '?' + paramString : '');
  }

  initFromURL() {
    const url = new URL(window.location);

    // Initialize price slider from URL
    if (this.minPriceSlider && this.maxPriceSlider) {
      const minPrice = url.searchParams.get('filter.v.price.gte');
      const maxPrice = url.searchParams.get('filter.v.price.lte');

      if (minPrice) {
        this.minPriceSlider.value = minPrice;
      }
      if (maxPrice) {
        this.maxPriceSlider.value = maxPrice;
      }

      this.updatePriceDisplay();
    }

    // Initialize sort buttons from URL
    const sortBy = url.searchParams.get('sort_by');
    if (sortBy) {
      this.sortButtons.forEach(btn => {
        if (btn.dataset.sort === sortBy) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
    }

    // Initialize checkboxes from URL query parameters
    const productTypes = url.searchParams.getAll('filter.p.product_type');
    const vendors = url.searchParams.getAll('filter.p.vendor');
    const colors = url.searchParams.getAll('filter.v.option.color');
    const sizes = url.searchParams.getAll('filter.v.option.size');
    const genders = url.searchParams.getAll('filter.v.option.gender');
    const tags = url.searchParams.getAll('filter.p.tag');

    this.filterCheckboxes.forEach(checkbox => {
      const filterType = checkbox.dataset.filterType;
      const value = checkbox.value;
      const originalValue = checkbox.dataset.originalValue || value;

      if (filterType === 'type') {
        if (productTypes.includes(originalValue)) {
          checkbox.checked = true;
        }
      } else if (filterType === 'vendor') {
        if (vendors.includes(originalValue)) {
          checkbox.checked = true;
        }
      } else if (filterType === 'color') {
        if (colors.includes(originalValue)) {
          checkbox.checked = true;
        }
      } else if (filterType === 'size') {
        if (sizes.includes(originalValue)) {
          checkbox.checked = true;
        }
      } else if (filterType === 'gender') {
        if (genders.includes(originalValue)) {
          checkbox.checked = true;
        }
      } else if (filterType === 'tag') {
        if (tags.includes(originalValue)) {
          checkbox.checked = true;
        }
      }
    });
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionFilters();
  });
} else {
  new CollectionFilters();
}
